import { Check, CornerUpLeft, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { JEV_BUDGET_APPROVAL_PRESETS, jevBudgetAmountProblem, jevBudgetLabel, type ApprovalDecision } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { JevBudgetPicker } from '../../../components/JevBudgetPicker';
import { Modal } from '../../../components/Modal';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useDecideApproval } from '../api/requisitions-api';
import styles from './DecisionDialog.module.css';

/**
 * REQ-05 — one approval decision (F4.03–F4.05). Opened from the queue, and from a
 * notification's deep link (`?decide=`), so an approver lands on the decision itself.
 *
 * Approve is one click. Reject and send back stay disabled until a reason is
 * written (non-negotiable #2); send back's is the comment the requester reads.
 *
 * ADR 0070: when the request asks for a Jev budget, the approver sees it already filled in, so approving as
 * asked is still one click, and "Change amount" opens the presets. Every amount is approved by someone.
 */
export function DecisionDialog({
  stepId,
  stepVersion,
  reference,
  title,
  onBehalfOfName,
  jevBudgetMicroUsd = null,
  onDecided,
  onClose,
}: {
  readonly stepId: string;
  readonly stepVersion: number;
  readonly reference: string;
  readonly title: string;
  readonly onBehalfOfName: string | null;
  /** The Jev budget as it stands now (the ask, or what an earlier approver made it); null when none was asked. */
  readonly jevBudgetMicroUsd?: number | null;
  /** Called once the server has accepted the decision — HM-02 times it (S10). */
  readonly onDecided?: (decision: ApprovalDecision) => void;
  readonly onClose: () => void;
}): ReactNode {
  const decide = useDecideApproval();
  const showToast = useToasts((state) => state.show);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<ApprovalDecision | null>(null);
  const [isChanging, setChanging] = useState(false);
  const [amount, setAmount] = useState<number | null>(jevBudgetMicroUsd);

  const hasBudget = jevBudgetMicroUsd !== null;
  // A typed amount below $1 or above $1,000 is called out in the picker and holds Approve off; it is never cut to the limit.
  const isAmountWrong = hasBudget && (amount === null || jevBudgetAmountProblem(amount) !== null);
  const isChanged = hasBudget && amount !== null && !isAmountWrong && amount !== jevBudgetMicroUsd;
  const approveLabel = isChanged ? `Approve with ${jevBudgetLabel(amount)} Jev budget` : 'Approve';

  async function run(decision: ApprovalDecision): Promise<void> {
    setPending(decision);
    try {
      await decide.mutateAsync({
        stepId,
        version: stepVersion,
        decision,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        // Only an approval carries the amount, and only when this approver changed it.
        ...(decision === 'approve' && isChanged && amount !== null ? { jevBudgetMicroUsd: amount } : {}),
      });
      showToast({ tone: 'success', message: `${reference} ${PAST[decision]}` });
      onDecided?.(decision);
      onClose();
    } catch (cause) {
      showToast({ tone: 'danger', message: 'That decision was not saved', detail: errorMessage(cause) });
    } finally {
      setPending(null);
    }
  }

  const hasReason = reason.trim() !== '';

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${reference} — ${title}`}
      {...(onBehalfOfName ? { description: `You are deciding on behalf of ${onBehalfOfName}, who is away.` } : {})}
      footer={
        <div className={styles.footer}>
          <Button
            variant="outline"
            icon={CornerUpLeft}
            disabled={!hasReason}
            isPending={pending === 'send_back'}
            onClick={() => void run('send_back')}
          >
            Send back
          </Button>
          <Button
            variant="danger"
            icon={X}
            disabled={!hasReason}
            isPending={pending === 'reject'}
            onClick={() => void run('reject')}
          >
            Reject
          </Button>
          <Button
            variant="solid"
            icon={Check}
            disabled={isAmountWrong}
            isPending={pending === 'approve'}
            onClick={() => void run('approve')}
          >
            {approveLabel}
          </Button>
        </div>
      }
    >
      {hasBudget && (
        <div className={styles.budget}>
          <span className={styles.budgetLabel}>Jev budget</span>
          {isChanging ? (
            <JevBudgetPicker label="Jev budget" value={amount} presets={JEV_BUDGET_APPROVAL_PRESETS} onChange={setAmount} />
          ) : (
            <div className={styles.budgetRow}>
              <span className={styles.budgetAmount}>{jevBudgetLabel(jevBudgetMicroUsd)}</span>
              <span className={styles.budgetNote}>As requested</span>
              <Button variant="ghost" size="sm" onClick={() => setChanging(true)}>
                Change amount
              </Button>
            </div>
          )}
        </div>
      )}
      <Field
        label="Reason or comment"
        isOptional
        hint="Needed to reject or send back. The requester reads it."
      >
        {({ inputId }) => (
          <Textarea
            id={inputId}
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
    </Modal>
  );
}

const PAST: Readonly<Record<ApprovalDecision, string>> = {
  approve: 'approved',
  reject: 'rejected',
  send_back: 'sent back',
};
